import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserType } from 'api/models';
import { TimedRender } from 'components/atoms/utils/timed-render';
import { useUser } from 'hooks/use-user';
import { IPINFO_URL, RH_EVENT_ORIGIN } from 'routes/vendor/constants';
import { CloseButton, injectRHWidget, IntroModalWidth, LoadingSpinner, modalMaskCSS, RevenueHeroState, RHContainer, RHTitle, StyledModal } from 'routes/vendor/intro-modal';
import { logger } from 'services';
const RH_TARGET = 'revenuehero_target';
const RhWidget = ({ rhData, close })=>{
    const { rhWidget: hero } = window;
    const loading = !hero || !rhData;
    const [rhHeight, setRHHeight] = useState(489);
    const [isBooking, setIsBooking] = useState(true);
    useEffect(()=>{
        if (loading) {
            return;
        }
        const messageEventCallback = (event)=>{
            var _event_data, _event_data1;
            if (event.origin !== RH_EVENT_ORIGIN || !event.isTrusted) {
                return;
            }
            if (((_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.type) === 'RESIZE_IFRAME') {
                setRHHeight(event.data.height + 4);
            } else if (((_event_data1 = event.data) === null || _event_data1 === void 0 ? void 0 : _event_data1.type) === 'MEETING_BOOKED') {
                setIsBooking(false);
            }
        };
        try {
            window.addEventListener('message', messageEventCallback);
            hero.dialog.setEmbedTarget(`#${RH_TARGET}`);
            hero.dialog.open(rhData);
        } catch (e) {
            logger.error('Unable to show Revenue Hero widget', {
                error: e
            });
            close();
        }
        // eslint-disable-next-line consistent-return
        return ()=>{
            window.removeEventListener('message', messageEventCallback);
        };
    }, [
        close,
        rhData,
        loading,
        hero.dialog
    ]);
    if (loading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {
            size: "large"
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(RHTitle, {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: isBooking && /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "signup_onboarding_rh"
                        })
                    }),
                    /*#__PURE__*/ _jsx(CloseButton, {
                        variant: "ghost",
                        type: "button",
                        onClick: close,
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: isBooking ? 'skip' : 'close'
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RHContainer, {
                id: RH_TARGET,
                height: rhHeight
            })
        ]
    });
};
const RhDemoModalContent = ({ rhState, setRhState, onFinish })=>{
    const { user: { userType, emailId } } = useUser();
    const [rhData, setRhData] = useState(null);
    useEffect(()=>{
        const _window = window;
        if (rhState === RevenueHeroState.Loading && userType !== UserType.Partner && window.rhWidget === undefined) {
            // Run only once
            injectRHWidget({
                emailId,
                rhState,
                setRhState,
                setRHData: setRhData,
                onFinish
            });
        } else {
            if (rhData !== null) {
                return;
            }
            (async ()=>{
                let country = '';
                try {
                    const ipResponse = await fetch(IPINFO_URL);
                    const ipData = await ipResponse.json();
                    country = ipData.country;
                } catch (e) {
                    logger.error('Unable to fetch IP address', {
                        error: e
                    });
                }
                _window.rhWidget.submit({
                    email: emailId,
                    ip_detected_country: country
                }).then((data)=>{
                    if (data) {
                        setRhData(data);
                        setRhState(RevenueHeroState.Qualified);
                    } else if (rhState === RevenueHeroState.Loading) {
                        // User was disqualified from the meeting
                        setRhState(RevenueHeroState.Disqualified);
                    } else {
                        // User was disqualified and the widget is being shown currently, so close it
                        onFinish();
                    }
                });
            })();
        }
    }, [
        emailId,
        rhState,
        setRhState,
        setRhData,
        onFinish,
        userType,
        rhData
    ]);
    if (rhState === RevenueHeroState.Qualified) {
        return /*#__PURE__*/ _jsx(TimedRender, {
            hideAfterMs: 2500,
            retainIf: ()=>rhData !== null,
            onHide: ()=>{
                logger.warn('Revenue Hero widget did not load in time');
                onFinish();
            },
            children: /*#__PURE__*/ _jsx(RhWidget, {
                rhData: rhData,
                close: onFinish
            })
        });
    }
    return null;
};
export const RhModal = ({ visible, close })=>{
    const [rhState, setRhState] = useState(RevenueHeroState.Loading);
    if (!visible) {
        return null;
    }
    return /*#__PURE__*/ _jsx(StyledModal, {
        width: rhState === RevenueHeroState.Qualified ? IntroModalWidth.RevenueHeroWidget : 0,
        open: visible,
        closable: false,
        footer: null,
        centered: true,
        maskStyle: modalMaskCSS,
        zIndex: 1012,
        destroyOnClose: true,
        children: /*#__PURE__*/ _jsx(RhDemoModalContent, {
            rhState: rhState,
            setRhState: setRhState,
            onFinish: close
        })
    });
};
